import { useState, useEffect } from 'react';
import { useAuthHeader, useAuth } from '@/hooks/useAuth';
import {
  getInstructionSheetConverterJobs,
  downloadConverterJobFile,
  type InstructionSheetConverterJob,
} from '@/lib/api/instruction-sheet-converter';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Loader2,
  Download,
  Eye,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  ChevronDown,
  ChevronUp,
  FileText,
  RefreshCw,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import type { SimpleAccount } from '@/lib/types';

// エラーメッセージの切り詰め用ユーティリティ
const truncateErrorMessage = (
  message: string,
  maxLength: number = 500,
): { truncated: string; isTruncated: boolean } => {
  if (!message || message.length <= maxLength) {
    return { truncated: message, isTruncated: false };
  }

  // 最後の改行位置を探して、そこで切り詰める（可読性向上）
  const truncated = message.substring(0, maxLength);
  const lastNewlineIndex = truncated.lastIndexOf('\n');
  const cutPoint = lastNewlineIndex > maxLength * 0.7 ? lastNewlineIndex : maxLength;

  return {
    truncated: message.substring(0, cutPoint) + '...',
    isTruncated: true,
  };
};

// エラーメッセージ表示コンポーネント
interface ErrorMessageDisplayProps {
  errorMessage: string;
}

function ErrorMessageDisplay({ errorMessage }: ErrorMessageDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { truncated, isTruncated } = truncateErrorMessage(errorMessage, 500);

  return (
    <div>
      <h3 className="font-semibold mb-2 text-red-600">エラーメッセージ</h3>
      <div className="bg-red-50 border border-red-200 rounded p-3">
        <pre className="whitespace-pre-wrap text-sm">{isExpanded ? errorMessage : truncated}</pre>
        {isTruncated && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-2 h-auto p-1"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-3 w-3 mr-1" />
                閉じる
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3 mr-1" />
                全て表示
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

interface InstructionSheetConverterJobsProps {
  account: SimpleAccount;
}

// ステータスアイコンとバッジの設定
const getStatusConfig = (status: string) => {
  switch (status) {
    case 'completed':
      return {
        variant: 'default' as const,
        className: 'bg-green-100 text-green-800 border-green-200',
        icon: CheckCircle,
        label: '完了',
      };
    case 'failed':
      return {
        variant: 'destructive' as const,
        className: 'bg-red-100 text-red-800 border-red-200',
        icon: XCircle,
        label: '失敗',
      };
    case 'running':
      return {
        variant: 'secondary' as const,
        className: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: Loader2,
        label: '実行中',
      };
    case 'pending':
    default:
      return {
        variant: 'outline' as const,
        className: 'bg-gray-100 text-gray-800 border-gray-200',
        icon: Clock,
        label: '待機中',
      };
  }
};

// ステータスバッジコンポーネント
function JobStatusBadge({ status }: { status: string }) {
  const config = getStatusConfig(status);
  const Icon = config.icon;

  return (
    <Badge
      variant={config.variant}
      className={`inline-flex items-center gap-1 ${config.className}`}
    >
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

// ダウンロードボタンコンポーネント
function DownloadButton({
  job,
  account,
  authHeaders,
  onSuccess,
  onError,
}: {
  job: InstructionSheetConverterJob;
  account: SimpleAccount;
  authHeaders: Record<string, string>;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}) {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    // ダウンロード可能性チェック（修正: result_files_nameの存在確認を削除）
    if (job.job_status !== 'completed') {
      onError?.('ジョブが完了していません');
      return;
    }

    setIsDownloading(true);

    try {
      await downloadConverterJobFile(account.id, job.id, authHeaders);
      onSuccess?.();
    } catch (error) {
      console.error('ダウンロードエラー:', error);
      const errorMessage = error instanceof Error ? error.message : 'ダウンロードに失敗しました';
      onError?.(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  const canDownload = job.job_status === 'completed';

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleDownload}
      disabled={!canDownload || isDownloading}
      title={canDownload ? '結果をダウンロード' : 'ダウンロード不可'}
    >
      {isDownloading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Download className="h-4 w-4" />
      )}
    </Button>
  );
}

export function InstructionSheetConverterJobs({ account }: InstructionSheetConverterJobsProps) {
  const { getAuthHeader } = useAuthHeader();
  const { toast } = useToast();

  const [jobs, setJobs] = useState<InstructionSheetConverterJob[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ジョブ一覧取得
  const loadJobs = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const authHeaders = getAuthHeader();
      const jobList = await getInstructionSheetConverterJobs(
        account.id,
        authHeaders,
        50, // 最大50件取得
      );
      setJobs(jobList);
    } catch (err) {
      console.error('ジョブ一覧取得エラー:', err);
      const errorMessage = err instanceof Error ? err.message : 'ジョブ一覧の取得に失敗しました';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // 初期化
  useEffect(() => {
    loadJobs();
  }, [account.id]);

  // ダウンロード成功時のハンドラー
  const handleDownloadSuccess = () => {
    toast({
      title: 'ダウンロード開始',
      description: 'ファイルのダウンロードを開始しました',
    });
  };

  // ダウンロードエラー時のハンドラー
  const handleDownloadError = (error: string) => {
    toast({
      title: 'ダウンロードエラー',
      description: error,
      variant: 'destructive',
    });
  };

  // 日時フォーマット関数
  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return format(date, 'yyyy/MM/dd HH:mm:ss', { locale: ja });
    } catch {
      return '-';
    }
  };

  // 実行時間フォーマット関数
  const formatDuration = (ms?: number) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}秒`;
    return `${(ms / 60000).toFixed(1)}分`;
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            指示書変換実行履歴
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={loadJobs}
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            更新
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {isLoading && jobs.length === 0 ? (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">履歴がありません</p>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ステータス</TableHead>
                  <TableHead>入力ファイル</TableHead>
                  <TableHead>実行者</TableHead>
                  <TableHead>開始日時</TableHead>
                  <TableHead>終了日時</TableHead>
                  <TableHead>実行時間</TableHead>
                  <TableHead>エラー</TableHead>
                  <TableHead className="text-center">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell>
                      <JobStatusBadge status={job.job_status} />
                    </TableCell>
                    <TableCell className="font-mono max-w-[200px] truncate">
                      {job.input_filename}
                    </TableCell>
                    <TableCell className="max-w-[150px] truncate">{job.executed_by}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatDateTime(job.execution_start_time)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatDateTime(job.execution_end_time)}
                    </TableCell>
                    <TableCell>{formatDuration(job.execution_duration_ms)}</TableCell>
                    <TableCell>
                      {job.error_message ? (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-auto p-1 text-red-600 hover:text-red-800"
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              エラー確認
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl">
                            <DialogHeader>
                              <DialogTitle>エラー詳細</DialogTitle>
                            </DialogHeader>
                            <ErrorMessageDisplay errorMessage={job.error_message} />
                          </DialogContent>
                        </Dialog>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <DownloadButton
                        job={job}
                        account={account}
                        authHeaders={getAuthHeader()}
                        onSuccess={handleDownloadSuccess}
                        onError={handleDownloadError}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
